import { LineChart } from "@/components/LineChart";
import {
  MultiMetricPicker,
  type PresetsBlock,
} from "@/components/MetricPicker";
import {
  aggregateSeries,
  findMetric,
  type MetricDef,
} from "@/lib/metrics";
import { longDate, shortDate, shortRange } from "@/lib/overview-helpers";
import { MIN_RANGE_LABEL_GAP } from "@/lib/chart-axis";
import {
  type ChartSeriesPoint,
  placeholderMetric,
} from "@/lib/entity-chart-metrics";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { ChangeMarkers, type DayMarker } from "./ChangeMarkers";
import { EmptyChart } from "./EmptyChart";

// Отступы поля внутри `LineChart` — 62 слева, 18 справа (62, когда есть
// вторая ось). Маркерам они нужны в ПРОЦЕНТАХ: без них полоса маркеров и
// линия считают координату по-разному и расходятся почти на семь
// процентов ширины.
//
// ★ Доля зависит от ширины `viewBox`, а она больше не константа: с
// `fitWidth` график рисуется в измеренных пикселях. Делить на жёсткие
// 900 значило бы на колонке 1400 px занизить левый отступ втрое — и
// маркеры уехали бы влево тем сильнее, чем шире экран.
const CHART_PAD_L = 62;
const CHART_PAD_R_MIN = 18;
const VIEWBOX_W_DEFAULT = 900;

export function ChartCard({
  series,
  metrics,
  onMetricsChange,
  extraMetrics,
  title,
  headerExtra,
  changeMarkers,
  loading = false,
  emptyText,
  presets,
  onlyMetrics,
  periodPicker,
  xEmphasis,
  markerPosition,
  fitWidth,
  chartHeight,
  refetching,
}: {
  // ★ Не `DailyPoint[]`: точка может нести охват группы, и без него
  // подписать агрегированный график нечем. Приведений здесь больше нет —
  // тип говорит правду о том, что читается.
  series: ChartSeriesPoint[];
  metrics: string[];
  onMetricsChange: (ids: string[]) => void;
  // Per-app slot MetricDefs (§36). Looked up before the static
  // METRICS registry so chart can resolve "tile:N" IDs to user-
  // configured events.
  extraMetrics?: MetricDef[];
  // ★ Всё ниже — НЕОБЯЗАТЕЛЬНОЕ, и умолчания повторяют прежнее
  // поведение до символа. Overview передаёт четыре первых пропа и не
  // знает об остальных: встроенный график Ads Manager не должен был
  // стоить правки экрана, который к нему отношения не имеет.
  //
  // Заголовок: у Overview это «Performance over time», у Ads Manager —
  // имя сущности с бейджем уровня.
  title?: ReactNode;
  // Правее кнопки Metrics. У Ads Manager там крестик «закрыть».
  headerExtra?: ReactNode;
  // Дни, когда сущность меняли через OneAds. Маркер один на день
  // независимо от числа правок.
  changeMarkers?: DayMarker[];
  loading?: boolean;
  // Текст пустого состояния. У Overview его нет — там рисуется
  // `EmptyChart`, и менять это незачем.
  emptyText?: string;
  // Блок пресетов в дропдауне метрик. Нет — нет и блока: у Overview
  // пресетов набора нет, и заводить их ради соседнего экрана незачем.
  presets?: PresetsBlock;
  // Ограничить дропдаун этим набором. Пусто — весь реестр, как раньше.
  onlyMetrics?: MetricDef[];
  // Переключатель агрегации — левее кнопки Metrics, как в макете.
  periodPicker?: ReactNode;
  // Какие подписи оси выделить (см. `LineChart.emphasis`).
  xEmphasis?: boolean[];
  /** Куда ставить маркер дня — доля 0..1 вдоль поля графика. Знает
   *  только вызывающий: при агрегации дни ложатся внутрь точек. */
  markerPosition?: (day: string) => number | null;
  /** ★ Рисовать в ИЗМЕРЕННЫХ пикселях, а не растягивать `viewBox`.
   *
   *  По умолчанию SVG отдаётся как `width="100%" viewBox="0 0 900 260"`:
   *  на широкой колонке он растягивается, и вместе с геометрией
   *  увеличивается ВЕСЬ текст внутри — подписи осей, значения, даты. На
   *  всю ширину Ads Manager это давало шрифт вдвое крупнее авторского и
   *  график высотой почти в пол-экрана.
   *
   *  Дизайн решает это иначе — фиксирует `height` и ставит
   *  `preserveAspectRatio="none"`, вынося ВЕСЬ текст из SVG отдельными
   *  `<span>`. Тот же итог достигается измерением ширины: когда
   *  `viewBox` совпадает с реальной, масштаб равен единице, шрифты
   *  остаются 10–12 px, а высота — ровно `chartHeight`. Разница в том,
   *  что здесь ничего не растягивается, поэтому не нужен ни
   *  `non-scaling-stroke`, ни вынос подписей наружу.
   *
   *  Overview этот признак НЕ передаёт и ведёт себя как прежде. */
  fitWidth?: boolean;
  chartHeight?: number;
  /** Данные уже есть, но идёт НОВЫЙ запрос (смена периода, метрик,
   *  сущности). Отдельно от `loading`: тот показывает скелетон вместо
   *  графика и годится только для первой загрузки — на перерисовке он
   *  выбросил бы готовую картинку. `isLoading` в TanStack при этом ЛОЖЕН
   *  (данные есть), поэтому без этого признака перерисовка выглядела как
   *  зависание. */
  refetching?: boolean;
}) {
  // ★ Никакой подмены. Раньше конец цепочки был `getMetric`, а он на
  // незнакомый идентификатор отдаёт `METRICS[0]` — Spend. Так метрика по
  // событию, которого у сущности за период не было (её определения нет
  // в `extraMetrics`), рисовалась как Spend: с его именем, его нулём и
  // его цветом, и легенда показывала Spend трижды. Теперь незнакомый
  // идентификатор — заглушка со своим именем и «—».
  const lookupMetric = (id: string): MetricDef => {
    if (extraMetrics) {
      const hit = extraMetrics.find((m) => m.id === id);
      if (hit) return hit;
    }
    return findMetric(id) ?? placeholderMetric(id);
  };
  // Метрики реестра Overview читают `DailyPoint` напрямую, и «нет
  // значения» у них означает ноль (день без выручки — выручка 0).
  // Метрики из `extraMetrics` — графика Ads Manager и плиток — отдают
  // `null`, когда величина не определена (CPT без тапов, метрика без
  // данных), и этот `null` обязан дойти до графика разрывом, а не лечь
  // на ось нулём.
  const isExtra = (id: string) =>
    extraMetrics?.some((m) => m.id === id) ?? false;
  // ★ Агрегированная точка подписывается ДИАПАЗОНОМ — всегда, а не
  // только когда группа неполная.
  //
  // Полная неделя раньше подписывалась одним днём: у неё начало группы
  // «и есть ответ». Оказалось — нет. При периоде «27 июля — 30 августа»
  // последняя недельная точка держит 24–30 августа, а подпись «Aug 24»
  // читается как «график тут и кончился», хотя 30-е в ней есть. Разницы
  // между полной и неполной группой читатель не знает и знать не должен:
  // вопрос у него один — какой отрезок под этой точкой.
  const spanOf = (p: ChartSeriesPoint) =>
    p.span_from && p.span_to && p.span_from !== p.span_to
      ? ([p.span_from, p.span_to] as const)
      : null;
  const labels = series.map((p) => {
    const sp = spanOf(p);
    return sp ? shortRange(sp[0], sp[1]) : shortDate(p.date);
  });
  // Подписи-диапазоны почти вдвое шире дневных, поэтому и разрежать их
  // надо шире: с прежним зазором они наезжали бы друг на друга.
  const anyRange = series.some((p) => spanOf(p) != null);
  const fullLabels = series.map((p) => {
    const sp = spanOf(p);
    return sp ? `${longDate(sp[0])} – ${longDate(sp[1])}` : longDate(p.date);
  });
  const resolvedSeries = metrics.map((id) => {
    const m = lookupMetric(id);
    const nullable = isExtra(id) || findMetric(id) == null;
    return {
      label: m.short,
      data: series.map((p) => {
        const v = m.fromSeries(p);
        return nullable ? v : v ?? 0;
      }),
      color: m.color,
    };
  });
  const colors = resolvedSeries.map((s) => s.color);
  // Axis assignment by MAGNITUDE CLASS, not by unit.
  //   VOLUME (isRate: false) → LEFT  — Spend, Revenue, Installs,
  //       Trials, Impressions, Taps. These are the "big numbers"
  //       that grow with campaign scale.
  //   RATE   (isRate: true)  → RIGHT — CPA, CPT, ROAS, CVR, TTR,
  //       Trial cost. These are small ratios that oscillate in a
  //       narrow band and get completely squashed if they share an
  //       axis with a volume metric.
  //
  // Earlier we split by unit ($), which kept CPA ($0.49) on the same
  // axis as Spend ($300); CPA was indistinguishable from the
  // baseline. Splitting by isRate gives each group its own scale,
  // matching "big on left / small on right" semantics directly.
  //
  // If the user picks only rates (e.g. "ROAS + CPA"), both would end
  // up on the right with an empty left axis — flip them all to left
  // so the chart still renders cleanly.
  let axes: ("left" | "right")[] = metrics.map((id) =>
    lookupMetric(id).isRate ? "right" : "left"
  );
  if (axes.every((a) => a === "right")) {
    axes = axes.map(() => "left");
  }
  // Rate metrics recompute from underlying sums; volume metrics sum.
  // aggregateSeries uses the static METRIC_MAP; for tile metrics we
  // compute the volume sum directly here since the slot's isRate is
  // always false (cpa is excluded from the chart upstream).
  const totals = metrics.map((id) => {
    const extra = extraMetrics?.find((m) => m.id === id);
    if (extra) {
      // ★ Отношение НЕЛЬЗЯ складывать. Комментарий выше объяснял, почему
      // сумма тут годилась: «у плиток isRate всегда false». Для плиток
      // Overview это верно, для метрик Ads Manager — нет: там есть TTR,
      // CPT, CR и CPA, и легенда показывала сумму тридцати дневных CPA
      // вместо CPA за период.
      //
      // Правильное значение считает сам определитель метрики по всему
      // ряду: `totalOf` складывает базовые величины и выводит отношение
      // из сумм — то же, что делает недельная агрегация.
      if (extra.totalOf) return extra.totalOf(series);
      let total = 0;
      let seen = 0;
      for (const p of series) {
        const v = extra.fromSeries(p);
        if (v != null) {
          total += v;
          seen += 1;
        }
      }
      return seen === 0 ? null : total;
    }
    return aggregateSeries(id, series);
  });

  // Ширина области построения. `null` до первого замера — до него
  // график рисуется прежним растягиванием, иначе первый кадр уходил бы
  // в нулевую ширину.
  const plotRef = useRef<HTMLDivElement>(null);
  const [plotW, setPlotW] = useState<number | null>(null);
  useLayoutEffect(() => {
    if (!fitWidth) return;
    const el = plotRef.current;
    if (!el) return;
    // ★ Первый замер — синхронно, до отрисовки. `ResizeObserver` вызывает
    // обработчик уже ПОСЛЕ первого кадра, и этот кадр рисовался прежним
    // растягиванием: измерено на стенде — масштаб 1,518, кегль 15,9 px,
    // холст 228 px вместо 150. Глазу это мигание крупного графика.
    const w0 = Math.round(el.getBoundingClientRect().width);
    if (w0 > 0) setPlotW(w0);
    const ro = new ResizeObserver(([e]) => {
      const w = Math.round(e?.contentRect.width ?? 0);
      if (w > 0) setPlotW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitWidth]);

  // Ширина, в которой график СЧИТАЕТ координаты. Одна и та же для линии
  // и для полосы маркеров — иначе они разъезжаются.
  const viewBoxW = (fitWidth && plotW) || VIEWBOX_W_DEFAULT;

  return (
    <div className="chart-box">
      <div className="card-head" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {title ?? <h3>Performance over time</h3>}
          <div
            className="chart-legend"
            style={{
              marginTop: 6,
              display: "flex",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            {metrics.map((id, i) => {
              const m = lookupMetric(id);
              return (
                <span
                  key={id}
                  style={{ fontSize: 12, color: "var(--c-text-2)" }}
                >
                  <span
                    className="metric-swatch"
                    style={{ background: m.color }}
                  />
                  {m.short}{" "}
                  <b style={{ color: "var(--c-text)" }}>
                    {(() => {
                      const v = totals[i] ?? null;
                      // Нет значения — одно «—», без «$» и «%»: единица
                      // принадлежит числу, которого здесь нет.
                      if (v == null) return "—";
                      const prefix = m.unit === "$" ? "$" : "";
                      const suffix = m.unit && m.unit !== "$" ? m.unit : "";
                      return prefix + m.fmt(v) + suffix;
                    })()}
                  </b>
                </span>
              );
            })}
          </div>
        </div>
        <div
          className="row gap-8"
          style={{ alignItems: "center", flexShrink: 0 }}
        >
          {periodPicker}
          <MultiMetricPicker
            values={metrics}
            onChange={onMetricsChange}
            extraMetrics={extraMetrics}
            presets={presets}
            onlyMetrics={onlyMetrics}
            min={1}
            max={5}
            title="Metrics on chart"
            trigger={({ toggle, count }) => (
              <button
                className="btn"
                onClick={toggle}
                style={{ fontSize: 12 }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="none"
                  style={{ color: "var(--c-text-3)" }}
                >
                  <rect
                    x="2"
                    y="3"
                    width="3"
                    height="10"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.3"
                  />
                  <rect
                    x="6.5"
                    y="3"
                    width="3"
                    height="10"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.3"
                  />
                  <rect
                    x="11"
                    y="3"
                    width="3"
                    height="10"
                    rx="1"
                    stroke="currentColor"
                    strokeWidth="1.3"
                  />
                </svg>
                <span style={{ marginLeft: 6 }}>Metrics</span>
                <span
                  style={{
                    marginLeft: 6,
                    background: "var(--c-surface-sunk)",
                    padding: "1px 6px",
                    borderRadius: 999,
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                  }}
                >
                  {count}
                </span>
              </button>
            )}
          />
          {headerExtra}
        </div>
      </div>
      <div
        ref={plotRef}
        className={refetching && !loading ? "chart-refetching" : undefined}
        data-loading-label="Preparing data…"
        style={{ padding: "10px 12px 12px" }}
      >
        {loading ? (
          <div
            className="chart-skeleton"
            // ★ Высота ПРЯМО, без CSS-переменной. Переменная требовала бы
            // объявления в стилях — иначе `test_no_stylesheet_references_a_
            // variable_that_does_not_exist` падает, и справедливо: значение
            // приходит из JS, а в стилях его нет, значит любой читающий CSS
            // видит ссылку в никуда. Посредник здесь ничего не давал.
            style={chartHeight ? { height: chartHeight } : undefined}
          >
            Loading chart…
          </div>
        ) : series.length === 0 ? (
          emptyText ? (
            <div className="chart-empty-state">
              <svg
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 19V5" />
                <path d="M4 19h16" />
                <path d="M7 15l4-4 3 3 5-6" />
              </svg>
              <div style={{ fontSize: 13.5 }}>{emptyText}</div>
            </div>
          ) : (
            <EmptyChart />
          )
        ) : (
          <LineChart
            width={viewBoxW}
            {...(chartHeight ? { height: chartHeight } : {})}
            labels={labels}
            series={resolvedSeries}
            colors={colors}
            axes={axes}
            formatValue={(si, v) => {
              const id = metrics[si];
              if (!id) return String(v);
              const m = lookupMetric(id);
              const prefix = m.unit === "$" ? "$" : "";
              const suffix = m.unit && m.unit !== "$" ? m.unit : "";
              return prefix + m.fmt(v) + suffix;
            }}
            emphasis={xEmphasis}
            labelMinGap={anyRange ? MIN_RANGE_LABEL_GAP : undefined}
            tooltipHeader={(i) => fullLabels[i] ?? labels[i] ?? ""}
          />
        )}
        {!loading && series.length > 0 && changeMarkers?.length ? (
          <ChangeMarkers
            markers={changeMarkers}
            positionOf={markerPosition ?? ((day) => {
              // Умолчание — дневной ряд: день ищется среди точек.
              const i = series.findIndex((p) => p.date === day);
              return i < 0 ? null : i / (series.length - 1 || 1);
            })}
            padLeftPct={(CHART_PAD_L / viewBoxW) * 100}
            padRightPct={
              ((axes.includes("right") ? CHART_PAD_L : CHART_PAD_R_MIN) /
                viewBoxW) *
              100
            }
          />
        ) : null}
      </div>
    </div>
  );
}
